import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';

export default function WorkSchedulesList() {
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [rules, setRules] = useState([]);
  const [ruleForm, setRuleForm] = useState({
    dayOfWeek: 'MONDAY',
    startTime: '08:00',
    endTime: '17:00',
    breakMinutes: 60,
  });
  const [ruleEditId, setRuleEditId] = useState(null);
  const [ruleError, setRuleError] = useState(null);
  const [ruleValidation, setRuleValidation] = useState(null);
  const [isRulesLoading, setIsRulesLoading] = useState(false);
  const [isRuleSaving, setIsRuleSaving] = useState(false);
  const [isRuleDeletingId, setIsRuleDeletingId] = useState(null);

  const dayOptions = useMemo(
    () => [
      { value: 'MONDAY', label: 'Lunes' },
      { value: 'TUESDAY', label: 'Martes' },
      { value: 'WEDNESDAY', label: 'Miercoles' },
      { value: 'THURSDAY', label: 'Jueves' },
      { value: 'FRIDAY', label: 'Viernes' },
      { value: 'SATURDAY', label: 'Sabado' },
      { value: 'SUNDAY', label: 'Domingo' },
    ],
    [],
  );

  const dayLabelMap = useMemo(
    () =>
      dayOptions.reduce((acc, day) => {
        acc[day.value] = day.label;
        return acc;
      }, {}),
    [dayOptions],
  );

  const getDayLabel = (value) => dayLabelMap[value] || value;

  useEffect(() => {
    const loadSchedules = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get('/schedules');
        setSchedules(response.data);
      } catch (err) {
        const message =
          err.response?.data?.message || 'No se pudieron cargar los horarios.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadSchedules();
  }, []);

  const handleDelete = async (scheduleId) => {
    const confirmDelete = window.confirm('¿Seguro que deseas eliminar este horario?');
    if (!confirmDelete) {
      return;
    }
    setDeleteId(scheduleId);
    setError(null);
    try {
      await axios.delete(`/schedules/${scheduleId}`);
      setSchedules((prev) => prev.filter((schedule) => schedule.id !== scheduleId));
    } catch (err) {
      const message =
        err.response?.data?.message || 'No se pudo eliminar el horario.';
      setError(message);
    } finally {
      setDeleteId(null);
    }
  };

  const openRulesModal = async (schedule) => {
    setActiveSchedule(schedule);
    setRulesModalOpen(true);
    setRuleError(null);
    setRuleEditId(null);
    setRuleForm({
      dayOfWeek: 'MONDAY',
      startTime: '08:00',
      endTime: '17:00',
      breakMinutes: 60,
    });
    setIsRulesLoading(true);
    try {
      const response = await axios.get(`/schedules/${schedule.id}/rules`);
      setRules(response.data);
    } catch (err) {
      const message =
        err.response?.data?.message || 'No se pudieron cargar las reglas.';
      setRuleError(message);
    } finally {
      setIsRulesLoading(false);
    }
  };

  const closeRulesModal = () => {
    setRulesModalOpen(false);
    setActiveSchedule(null);
    setRules([]);
  };

  const handleRuleChange = (event) => {
    const { name, value } = event.target;
    setRuleForm((prev) => ({ ...prev, [name]: value }));
    setRuleValidation(null);
  };

  const handleEditRule = (rule) => {
    setRuleEditId(rule.id);
    setRuleForm({
      dayOfWeek: rule.dayOfWeek,
      startTime: rule.startTime,
      endTime: rule.endTime,
      breakMinutes: rule.breakMinutes ?? 0,
    });
  };

  const resetRuleForm = () => {
    setRuleEditId(null);
    setRuleForm({
      dayOfWeek: 'MONDAY',
      startTime: '08:00',
      endTime: '17:00',
      breakMinutes: 60,
    });
  };

  const saveRule = async (event) => {
    event.preventDefault();
    if (!activeSchedule) {
      return;
    }
    setIsRuleSaving(true);
    setRuleError(null);
    if (ruleForm.startTime >= ruleForm.endTime) {
      setRuleValidation('La hora de inicio debe ser menor que la hora de fin.');
      setIsRuleSaving(false);
      return;
    }
    if (!ruleEditId) {
      const dayExists = rules.some((rule) => rule.dayOfWeek === ruleForm.dayOfWeek);
      if (dayExists) {
        setRuleValidation('Ya existe una regla para ese dia.');
        setIsRuleSaving(false);
        return;
      }
    } else {
      const dayExists = rules.some(
        (rule) => rule.dayOfWeek === ruleForm.dayOfWeek && rule.id !== ruleEditId,
      );
      if (dayExists) {
        setRuleValidation('Ya existe una regla para ese dia.');
        setIsRuleSaving(false);
        return;
      }
    }
    try {
      const payload = {
        dayOfWeek: ruleForm.dayOfWeek,
        startTime: ruleForm.startTime,
        endTime: ruleForm.endTime,
        breakMinutes: Number(ruleForm.breakMinutes) || 0,
      };
      if (ruleEditId) {
        await axios.patch(`/schedules/${activeSchedule.id}/rules/${ruleEditId}`, payload);
      } else {
        await axios.post(`/schedules/${activeSchedule.id}/rules`, payload);
      }
      const response = await axios.get(`/schedules/${activeSchedule.id}/rules`);
      setRules(response.data);
      resetRuleForm();
    } catch (err) {
      const message =
        err.response?.data?.message || 'No se pudo guardar la regla.';
      setRuleError(message);
    } finally {
      setIsRuleSaving(false);
    }
  };

  const deleteRule = async (ruleId) => {
    if (!activeSchedule) {
      return;
    }
    const confirmDelete = window.confirm('¿Seguro que deseas eliminar esta regla?');
    if (!confirmDelete) {
      return;
    }
    setIsRuleDeletingId(ruleId);
    setRuleError(null);
    try {
      await axios.delete(`/schedules/${activeSchedule.id}/rules/${ruleId}`);
      const response = await axios.get(`/schedules/${activeSchedule.id}/rules`);
      setRules(response.data);
    } catch (err) {
      const message =
        err.response?.data?.message || 'No se pudo eliminar la regla.';
      setRuleError(message);
    } finally {
      setIsRuleDeletingId(null);
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Horarios de trabajo</h1>
          <p className="text-sm text-gray-500">Gestiona turnos y reglas por dia.</p>
        </div>
        <button
          onClick={() => navigate('/home/horarios/nuevo')}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Nuevo horario
        </button>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</div>}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="p-6 text-sm text-gray-500">Cargando horarios...</div>
        ) : schedules.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No hay horarios registrados.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Departamento ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Reglas
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Empleados
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {schedules.map((schedule) => (
                <tr key={schedule.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{schedule.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{schedule.type}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{schedule.status}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {schedule.departmentId ?? '--'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {schedule.rules?.length ?? 0}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {schedule.employees?.length ?? 0}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => navigate(`/home/horarios/editar/${schedule.id}`)}
                        className="rounded bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => openRulesModal(schedule)}
                        className="rounded bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700"
                      >
                        Reglas
                      </button>
                      <button
                        onClick={() => handleDelete(schedule.id)}
                        disabled={deleteId === schedule.id}
                        className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                      >
                        {deleteId === schedule.id ? 'Eliminando...' : 'Eliminar'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {rulesModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl rounded-lg bg-white shadow-lg">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Reglas del horario {activeSchedule?.name}
                </h2>
                <p className="text-xs text-gray-500">Configura las reglas por dia.</p>
              </div>
              <button
                type="button"
                onClick={closeRulesModal}
                className="rounded-lg px-2 py-1 text-sm text-gray-500 hover:bg-gray-100"
              >
                Cerrar
              </button>
            </div>
            <div className="grid gap-6 px-6 py-4 lg:grid-cols-3">
              <form onSubmit={saveRule} className="space-y-3 lg:col-span-1">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Dia</label>
                  <select
                    name="dayOfWeek"
                    value={ruleForm.dayOfWeek}
                    onChange={handleRuleChange}
                    className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                  >
                    {dayOptions.map((day) => (
                      <option key={day.value} value={day.value}>
                        {day.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Inicio</label>
                  <input
                    type="time"
                    name="startTime"
                    value={ruleForm.startTime}
                    onChange={handleRuleChange}
                    className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Fin</label>
                  <input
                    type="time"
                    name="endTime"
                    value={ruleForm.endTime}
                    onChange={handleRuleChange}
                    className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Descanso (min)</label>
                  <input
                    type="number"
                    name="breakMinutes"
                    value={ruleForm.breakMinutes}
                    onChange={handleRuleChange}
                    className="mt-1 w-full rounded-lg border border-gray-200 p-2"
                  />
                </div>
                {ruleError && (
                  <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">
                    {ruleError}
                  </div>
                )}
                {ruleValidation && (
                  <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">
                    {ruleValidation}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button
                    type="submit"
                    disabled={isRuleSaving}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {isRuleSaving ? 'Guardando...' : ruleEditId ? 'Guardar' : 'Agregar'}
                  </button>
                  {ruleEditId && (
                    <button
                      type="button"
                      onClick={resetRuleForm}
                      className="rounded-lg border border-gray-200 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              </form>

              <div className="lg:col-span-2">
                {isRulesLoading ? (
                  <div className="text-sm text-gray-500">Cargando reglas...</div>
                ) : rules.length === 0 ? (
                  <div className="text-sm text-gray-500">No hay reglas registradas.</div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                          Dia
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                          Inicio
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                          Fin
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                          Descanso
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {rules.map((rule) => (
                        <tr key={rule.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2">{getDayLabel(rule.dayOfWeek)}</td>
                          <td className="px-3 py-2">{rule.startTime}</td>
                          <td className="px-3 py-2">{rule.endTime}</td>
                          <td className="px-3 py-2">{rule.breakMinutes ?? 0} min</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditRule(rule)}
                                className="rounded bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200"
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteRule(rule.id)}
                                disabled={isRuleDeletingId === rule.id}
                                className="rounded bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                              >
                                {isRuleDeletingId === rule.id ? 'Eliminando...' : 'Eliminar'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
